import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { useEphemeralWorkspaceState } from "@/shared/components/visualization/WorkspaceCtx";
import { MessagePathNode } from "@/shared/domain/topics";
import { Dimensions } from "@/shared/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { usePanelState } from "@/shared/state/visualization";
import { isPlotPanelState } from "@/shared/state/visualization/schema";

import { Extents } from "../../PlotRenderer";
import { pixelRegionToTimeRange } from "../chartRegionToTimeRange";

import {
  generatePythonSnippet,
  type FileTopicConfig,
} from "./generatePythonSnippet";

interface SdkSnippetFromSelectionButtonProps {
  panelId: string;
  selectedRegion: Dimensions;
  chartExtents: Extents;
}

export function SdkSnippetFromSelectionButton({
  chartExtents,
  panelId,
  selectedRegion,
}: SdkSnippetFromSelectionButtonProps) {
  const timeRange = pixelRegionToTimeRange(selectedRegion, chartExtents);
  const panelState = usePanelState(panelId);
  const workspaceState = useEphemeralWorkspaceState();
  const [tooltipText, setTooltipText] = React.useState("Copy SDK snippet");

  const handleClick = () => {
    // Generate SDK snippet based on the selected time range and panel data
    const snippet = generateSdkSnippet(
      timeRange.startTime,
      timeRange.endTime,
      panelState,
      workspaceState.files,
    );

    // Copy to clipboard
    navigator.clipboard
      .writeText(snippet)
      .then(() => {
        setTooltipText("Copied!");
        setTimeout(() => setTooltipText("Copy SDK snippet"), 2000);
      })
      .catch((error) => {
        setTooltipText("Failed to copy!");
        setTimeout(() => setTooltipText("Copy SDK snippet"), 2000);
        ErrorMonitoringService.captureError(error);
      });
  };

  return (
    <RobotoTooltip title={tooltipText}>
      <span>
        <RobotoButton
          eventName={"SdkSnippetFromSelectionClicked"}
          variant="text"
          onClick={handleClick}
        >
          Copy Code
        </RobotoButton>
      </span>
    </RobotoTooltip>
  );
}

/**
 * Generates an SDK code snippet for the selected time range and message paths
 */
function generateSdkSnippet(
  startTime: bigint,
  endTime: bigint,
  panelState: ReturnType<typeof usePanelState>,
  files: ReturnType<typeof useEphemeralWorkspaceState>["files"],
): string {
  // Convert bigint nanoseconds to seconds with decimal precision
  const startTimeSec = Number(startTime) / 1e9;
  const endTimeSec = Number(endTime) / 1e9;

  // Group series by file ID, then by topic
  const fileMap = new Map<string, FileTopicConfig>();

  if (panelState && isPlotPanelState(panelState)) {
    panelState.data.forEach((series) => {
      const fileId = series.data.topic.association.association_id;
      const topicName = series.data.topic.name;
      const messagePath = MessagePathNode.partsToString(
        series.data.messagePath.parts,
      );

      // Get or create file config
      if (!fileMap.has(fileId)) {
        const fileRecord = files.find((f) => f.file_id === fileId);
        fileMap.set(fileId, {
          datasetId: fileRecord?.association_id ?? "<dataset_id>",
          filename: fileRecord?.name ?? "<file_name>",
          topics: new Map(),
        });
      }

      const fileConfig = fileMap.get(fileId)!;

      // Add topic and field to this file's config
      if (!fileConfig.topics.has(topicName)) {
        fileConfig.topics.set(topicName, new Set());
      }
      fileConfig.topics.get(topicName)!.add(messagePath);
    });
  }

  // Generate Python snippet using the extracted file configurations
  const fileConfigs = Array.from(fileMap.values());
  return generatePythonSnippet(fileConfigs, startTimeSec, endTimeSec);
}
